<template>
  <div class="w-full flex flex-col items-center">
    <!-- View Header -->
    <div class="w-full flex items-center p-1 bg-neutral-25" style="border-radius: 10px;">
      <div class="w-8 h-8 flex items-center justify-center">
        <CreativeTestsIcon class="text-icon-muted" />
      </div>
      <BaseText type="label" size="sm" class="text-text-muted ml-1.5">
        Creative Tests
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal ml-3">
        Analyze groups of ads launched together on a single day.
      </BaseText>
    </div>
    <!-- Loading State -->
    <div v-if="loading" class="flex flex-col items-center justify-center h-full pt-10">
      <BaseLoadingLogo :margin="2" />
    </div>
    <div v-else-if="pinsLoaded" class="flex flex-col items-center w-full" :class="{ 'fade-in': !locked }">
      <!-- Pinned Creative Tests -->
      <div v-if="!locked" class="flex flex-row justify-between pr-3 items-center w-full mt-3 border-t border-border-normal pt-3">
        <div class="flex flex-row items-center">
          <div class="flex flex-row gap-3 px-4 py-1 items-center">
            <img src="../../../assets/icons/unpinned.svg">
            <BaseText type="label" size="sm" class="text-text-muted">
              Pinned Creative Tests
            </BaseText>
            <BaseText v-if="pinnedCreativeTestDays.length === 0" type="body" size="xs" class="text-neutral-500">
              You don't have any creative tests pinned.
            </BaseText>
            <BaseText v-else-if="pinnedCreativeTestDays.length === 1" type="body" size="xs" class="text-text-muted">
              You have {{ pinnedCreativeTestDays.length }} creative test pinned.
            </BaseText>
            <BaseText v-else type="body" size="xs" class="text-text-muted">
              You have {{ pinnedCreativeTestDays.length }} creative tests pinned.
            </BaseText>
          </div>
        </div>
        <transition>
          <button v-if="pinnedCreativeTestDays.length > 0" class="flex flex-row px-2 gap-1.5 rounded-md transition-colors hover:bg-neutral-25"
          @click.stop.prevent="clearPins()">
            <UnpinIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Clear Pins
            </BaseText>
          </button>
        </transition>
      </div>

      <!-- Creative Test Days (PINNED) -->
      <div class="w-full" :class="{'border-b': pinnedCreativeTestDays.length > 0}">
        <transition-group>
          <CreativeTestDay 
            v-for="(pinnedCreativeTestDay, index) in pinnedCreativeTestDays"
            :key="`pinned-ct-${index}-${pinnedCreativeTestDay.date}`" 
            :class="{'mb-5': index === pinnedCreativeTestDays.length - 1}"
            :creative-test-day="pinnedCreativeTestDay"
            is-pinned
            @openDetailsDrawer="handleOpenAdvertisementDetailsDrawer($event)"
            @handlePin="pinOrUnpinCreativeTestDay(pinnedCreativeTestDay)"
          />
        </transition-group>
      </div>

      <!-- Creative Test Days (UNPINNED) -->
      <div class="w-full">
        <transition-group>
          <CreativeTestDay 
            v-for="(creativeTestDay, index) in creativeTestDays"
            :key="`ct-${index}-${creativeTestDay.date}`"
            :class="{ 'mb-10': index === creativeTestDays.length - 1 }"
            :creative-test-day="creativeTestDay"
            :init-expanded="locked && index === 0"
            :locked="locked"
            :brand-name="brand?.name"
            @openDetailsDrawer="handleOpenAdvertisementDetailsDrawer($event)"
            @handlePin="pinOrUnpinCreativeTestDay(creativeTestDay)"
          />
        </transition-group>
        <!-- Add infinite scroll here... -->
        <infinite-loading
          v-if="creativeTestDays.length && !locked"
          :identifier="infiniteId"
          @infinite="getMoreCreativeTests"
        >
          <div slot="spinner">
            <BaseLoadingLogo :margin="2" />
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
      </div>

      <AdvertisementDetailsDrawer
        v-if="showAdvertisementDetailsDrawer"
        :advertisement="selectedAdvertisement"
        discovery
        @getMoreAds="$emit('getMoreAds')"
        @close="closeDrawer"
        @save="$emit('reload')"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import AdvertisementDetailsDrawer from '../../advertisements/AdvertisementDetailsDrawer.vue'
import InfiniteLoading from 'vue-infinite-loading'
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'
import { getCreativeTestDaysMockData } from '@/utils/spyder/mockDataUtils'

import CreativeTestDay from './CreativeTestDay.vue'

// Icons
import UnpinIcon from '../../globals/Icons/SpyderV2Icons/UnpinIcon.vue'
import CreativeTestsIcon from '@/components/globals/Icons/SpyderModeIcons/CreativeTestsIcon.vue'

export default {
  name: 'CreativeTests',
  components: {
    CreativeTestDay,
    AdvertisementDetailsDrawer,
    InfiniteLoading,
    UnpinIcon,
    CreativeTestsIcon
  },
  props: {
    noDrawer: {
      type: Boolean,
      default: () => false
    },
    brand: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      creativeTestDays: [],
      pinnedDates: new Set(),
      pinnedCreativeTestDays: [],
      selectedAdvertisement: {},

      infiniteId: +new Date(),
      page: 1,
      totalPages: 1,

      loading: true,
      showAdvertisementDetailsDrawer: false,
      pinsLoaded: false,
      unpinning: false
    }
  },
  watch: {
    brand: {
      handler (brand) {
        console.log('brand watch', brand)
        this.resetState()
        if (!brand?.id) return
        this.initCreativeTests()
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser']),
    locked () {
      const isSubscribed = this.brand?.is_subscribed
      return isSubscribed === undefined ? false : !isSubscribed
    }
  },
  methods: {
    ...mapMutations('AdvertisementsModule', ['SET_AD_TO_OPEN']),
    async initCreativeTests () {
      if (!this.brand?.id) return
      console.log('initCreativeTests', this.brand)
      await this.fetchCreativeTests()
      await this.fetchPinnedCreativeTestDays()
      this.loading = false
    },
    async fetchCreativeTests () {
      if (!this.brand?.id) return
      if (!this.brand?.is_subscribed) {
        this.creativeTestDays = getCreativeTestDaysMockData()
        this.totalPages = 1
        this.pinsLoaded = true
        return
      }
      try {
        // Here we need to hit the API and get these groups
        const { data: runningDays, pages: totalPages } = await ForeplayAPI.Brands.getCreativeTests(this.brand.id)

        this.creativeTestDays = normalizeCreativeTestDays(runningDays)
        this.totalPages = totalPages
      } catch (error) {
        console.error('Error fetching creative tests', error)
      } finally {
        this.infiniteId += 1
      }
    },
    async getMoreCreativeTests ($state) {
      if (this.locked || !this.brand?.id) return
      try {
        if (this.page >= this.totalPages) {
          if (Object.keys($state).length) {
            $state.complete()
          }
          return
        }
        const { data: runningDays } = await ForeplayAPI.Brands.getCreativeTests(
          this.$route.params.id,
          { page: this.page + 1 }
        )

        const filteredRunningDays = normalizeCreativeTestDays(runningDays).filter(day => !this.pinnedDates.has(day.date))
        this.creativeTestDays = this.creativeTestDays.concat(filteredRunningDays)
        this.page += 1

        if (this.page >= this.totalPages) {
          if (Object.keys($state).length) {
            $state.complete()
          }
          return
        }

        $state.loaded()
      } catch (error) {
        console.log('Error fetching more creative tests', error)
      } finally {
        this.infiniteId += 1
      }
    },
    async fetchPinnedCreativeTestDays () {
      if (!this.brand?.id || this.locked) return
      try {
        const pinnedDays = await FirebaseAPI.Spyder.getPinnedCreativeTestDays(this.brand.id)
        if (!pinnedDays?.length) {
          this.pinsLoaded = true
          return
        }

        this.pinnedDates = new Set(pinnedDays)
        this.creativeTestDays = this.creativeTestDays.filter(creativeTestDay => !this.pinnedDates.has(creativeTestDay.date))

        const { data: pinnedFromElastic } = await ForeplayAPI.Brands.getCreativeTests(
          this.$route.params.id, 
          { dates: pinnedDays, limit: 100 }
        )

        this.pinnedCreativeTestDays = normalizeCreativeTestDays(pinnedFromElastic)
        this.pinnedCreativeTestDays.sort(sortByDate)
        this.pinsLoaded = true
      } catch (error) {
        console.log('Error fetching pinned creative test days', error)
      }
    },
    clearPins () {
      this.pinnedCreativeTestDays.forEach((day) => {
        this.pinOrUnpinCreativeTestDay(day)
      })
    },
    pinOrUnpinCreativeTestDay (creativeTestDay) {
      if (this.locked || !this.brand?.id) return
      const date = creativeTestDay.date
      if (this.pinnedDates.has(date)) {
        // Unpin the creative test day
        try {
          FirebaseAPI.Spyder.unpinCreativeTestDay(this.brand.id, date)
        } catch (error) {
          console.log('Error unpinning creative test day', error)
        }
        this.pinnedCreativeTestDays = this.pinnedCreativeTestDays.filter(day => day.date !== date)
        this.pinnedDates.delete(date)
        const indexToInsert = this.creativeTestDays.findIndex(day => date > day.date)
        if (indexToInsert !== -1) this.creativeTestDays.splice(indexToInsert, 0, creativeTestDay)
      } else {
        // Pin the creative test day
        try {
          FirebaseAPI.Spyder.pinCreativeTestDay(this.brand.id, date)
        } catch (error) {
          console.log('Error pinning creative test day', error)
        }
        this.creativeTestDays = this.creativeTestDays.filter(day => day.date !== date)
        this.pinnedDates.add(date)
        const indexToInsert = this.pinnedCreativeTestDays.findIndex(day => date > day.date)
        if (indexToInsert !== -1) this.pinnedCreativeTestDays.splice(indexToInsert, 0, creativeTestDay)
        else this.pinnedCreativeTestDays.push(creativeTestDay)
      }
    },
    handleOpenAdvertisementDetailsDrawer (advertisement) {
      if (this.locked) return
      this.selectedAdvertisement = advertisement
      this.showAdvertisementDetailsDrawer = true
    },
    closeDrawer () {
      this.showAdvertisementDetailsDrawer = false
      this.selectedAdvertisement = {}
      this.SET_AD_TO_OPEN({})
    },
    resetState () {
      this.loading = true
      this.pinsLoaded = false
      this.creativeTestDays = []
      this.pinnedDates = new Set()
      this.pinnedCreativeTestDays = []
      this.page = 1
      this.totalPages = 1
    },
  }
}

// Dates are 'YYYY-MM-DD' strings, which sort lexicographically
const sortByDate = (a, b) => b.date.localeCompare(a.date)

const normalizeCreativeTestDays = (creativeTestDays) => {
  return creativeTestDays.map((day) => ({
    date: day.date,
    liveCount: day.active_count,
    count: day.launched_count
  }))
}
</script>

<style scoped>
.v-enter-active, .v-leave-active {
  transition: opacity 200ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-in {
  animation: fadeIn 200ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>